import { Box, Modal, Typography, LinearProgress } from '@mui/material';
import { useEffect, useState } from 'react';

export default function ImageAnalysisModal({ open, fullscreen = false, imageUrl, analysis, nutrition, onAccept, onEdit, loading = false, validez, comentario, ingredientesFinales }: {
  open: boolean,
  fullscreen?: boolean,
  imageUrl?: string,
  analysis?: string,
  nutrition?: any,
  onAccept?: () => void,
  onEdit?: () => void,
  loading?: boolean,
  validez?: string,
  comentario?: string,
  ingredientesFinales?: string[]
}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open && loading) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress((old) => {
          if (old >= 95) return 95;
          return old + Math.random() * 3 + 1;
        });
      }, 200);
      return () => clearInterval(interval);
    }
  }, [open, loading]);

  useEffect(() => {
    if (!open || !loading) setProgress(0);
  }, [open, loading]);

  return (
    <Modal open={open} aria-labelledby="modal-analizando-imagen" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
      <Box sx={{
        bgcolor: 'white',
        p: fullscreen ? 0 : 4,
        borderRadius: fullscreen ? 0 : 3,
        boxShadow: fullscreen ? 'none' : '0 8px 32px rgba(0,0,0,0.1)',
        minWidth: fullscreen ? '100vw' : 320,
        minHeight: fullscreen ? '100vh' : undefined,
        maxWidth: fullscreen ? '100vw' : 400,
        width: fullscreen ? '100vw' : undefined,
        height: fullscreen ? '100vh' : undefined,
        textAlign: 'center',
        border: fullscreen ? 'none' : '1px solid',
        borderColor: fullscreen ? 'transparent' : 'primary.light',
        position: 'relative',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: fullscreen ? 'center' : undefined
      }}>
        {loading ? (
          <>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: 'primary.main',
              borderRadius: '3px 3px 0 0'
            }} />
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                <img
                  src="/imgs_ayuda_img_ok.jpg"
                  alt="Verdura animada"
                  style={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: '50%',
                    animation: 'bounce 1.5s ease-in-out infinite'
                  }}
                />
              </Box>
            </Box>
            <Typography variant="h6" mb={2} color="primary.main" fontWeight={600}>
              Analizando tu imagen...
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Detectando alimentos y nutrientes en tu plato
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 1,
                fontSize: '0.875rem',
                color: 'text.secondary'
              }}>
                <span>Procesando...</span>
                <span>{Math.round(progress)}%</span>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'primary.main',
                    borderRadius: 4,
                    transition: 'transform 0.3s ease'
                  }
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Esto puede tardar unos segundos
            </Typography>
          </>
        ) : (
          <Box sx={{ width: '100%', height: '100%', p: fullscreen ? 4 : 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {imageUrl && (
              <Box sx={{ mb: 3, width: fullscreen ? '60vw' : 200, maxWidth: '90vw', maxHeight: fullscreen ? '60vh' : 300, mx: 'auto', borderRadius: 4, overflow: 'hidden', boxShadow: 2 }}>
                <img src={imageUrl} alt="Plato subido" style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain' }} />
              </Box>
            )}
            {analysis && (
              <Typography variant="h6" color="primary.main" fontWeight={700} mb={2}>
                Análisis del plato
              </Typography>
            )}
            {analysis && (
              <Typography variant="body1" color="text.secondary" mb={2} sx={{ whiteSpace: 'pre-line' }}>
                {analysis}
              </Typography>
            )}
            {nutrition && (
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  Información Nutricional (aprox.)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(nutrition).map(([k, v]) => (
                    <Typography key={k} variant="body2" color="text.secondary">
                      {k.charAt(0).toUpperCase() + k.slice(1)}: {String(v)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {validez && (
              <Typography variant="subtitle2" color={validez === 'válido' ? 'success.main' : 'warning.main'} mb={1}>
                {validez === 'válido' ? 'Validación exitosa' : '¡Revisa tu selección!'}
              </Typography>
            )}
            {comentario && (
              <Typography variant="body2" color="text.secondary" mb={1}>{comentario}</Typography>
            )}
            {ingredientesFinales && ingredientesFinales.length > 0 && (
              <Typography variant="body2" color="primary.main" mb={1}><b>Ingredientes finales:</b> {ingredientesFinales.join(', ')}</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
              {onAccept && <button style={{ padding: '10px 24px', borderRadius: 8, background: '#388e3c', color: 'white', fontWeight: 600, border: 'none', fontSize: 16, cursor: 'pointer' }} onClick={onAccept}>Aceptar</button>}
              {onEdit && <button style={{ padding: '10px 24px', borderRadius: 8, background: '#fff', color: '#388e3c', fontWeight: 600, border: '2px solid #388e3c', fontSize: 16, cursor: 'pointer' }} onClick={onEdit}>Editar ingredientes</button>}
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
} 